import ChoiceChip from '@liveauctioneers/caterwaul-components/lib/ChoiceChips/ChoiceChips';
import { FilterChip } from '@liveauctioneers/caterwaul-components/lib/FilterChip/FilterChip';
import Text from '@liveauctioneers/caterwaul-components/lib/Text/Text';
import theme from '@liveauctioneers/caterwaul-components/lib/theme/theme';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import React, { ReactElement, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

import useIsWiderThan from '@/hooks/useIsWiderThan';
import FilterTerm from '@/pages/Search/Components/FilterTerm';
import { useAppSelector } from '@/redux/hooks';
import { getLocale } from '@/redux/modules/intl';

export type AuctionsNearMeFilterBarProps = {
    numberOfAuctions: number;
    onKeywordsChange: (keywords: string[]) => void;
    toggleLocalPickUpFilter: () => void;
    toggleTopRatedFilter: () => void;
};

const AuctionsNearMeFilterBar = ({
    numberOfAuctions,
    onKeywordsChange,
    toggleLocalPickUpFilter,
    toggleTopRatedFilter,
}: AuctionsNearMeFilterBarProps): ReactElement => {
    const intl = useIntl();
    const locale = useAppSelector(getLocale);
    const filterByKeywordPlaceholder = intl.formatMessage({
        id: 'filter_by_keyword',
    });
    const [keywords, setKeywords] = useState<string[]>([]);
    const isMobile = !useIsWiderThan(theme.breakpoints.tabletNarrowWidthNum);

    const addKeyword = (keyword: string): void => {
        const keywordAlreadyExists = keywords.find(
            (existingKeyword) => existingKeyword.toLowerCase() === keyword.toLowerCase()
        );
        if (!keywordAlreadyExists) {
            setKeywords([...keywords, keyword]);
        }
    };

    const removeKeyword = (keyword: string): void => {
        const keywordsClone = keywords.slice();
        keywordsClone.splice(keywordsClone.indexOf(keyword), 1);
        setKeywords(keywordsClone);
    };

    useEffect(() => {
        onKeywordsChange(keywords);
    }, [keywords, onKeywordsChange]);

    const hasKeywordFilters = keywords.length > 0;

    const desktopLayout = !isMobile ? (
        <StyledDesktopLayout>
            <StyledDesktopInputs>
                <FilterTerm
                    placeholderMessage={filterByKeywordPlaceholder}
                    shortOnMobile
                    submitFilterTerm={addKeyword}
                />
                <StyledFilterChipsContainer>
                    <ChoiceChip onClick={toggleTopRatedFilter}>
                        <FormattedMessage id="topRatedCount" />
                    </ChoiceChip>
                    <ChoiceChip onClick={toggleLocalPickUpFilter}>
                        <FormattedMessage id="freeLocalPickupCount" />
                    </ChoiceChip>
                </StyledFilterChipsContainer>
            </StyledDesktopInputs>
            {hasKeywordFilters && (
                <>
                    {keywords.map((keyword) => (
                        <StyledKeywordChip key={`auctions-near-me-keyword-filter-${keyword}`}>
                            <StyledFilterChip
                                key={`auctions-near-me-keyword-filter-${keyword}`}
                                onRemove={() => {
                                    removeKeyword(keyword);
                                }}
                            >
                                {keyword}
                            </StyledFilterChip>
                        </StyledKeywordChip>
                    ))}
                </>
            )}
        </StyledDesktopLayout>
    ) : null;

    const mobileLayout = isMobile ? (
        <span>
            <StyledMobileInputs>
                <FilterTerm
                    placeholderMessage={filterByKeywordPlaceholder}
                    shortOnMobile
                    submitFilterTerm={addKeyword}
                />
                <StyledFilterChipsContainer>
                    <ChoiceChip onClick={toggleTopRatedFilter}>
                        <FormattedMessage id="topRatedCount" />
                    </ChoiceChip>
                    <ChoiceChip onClick={toggleLocalPickUpFilter}>
                        <FormattedMessage id="freeLocalPickupCount" />
                    </ChoiceChip>
                </StyledFilterChipsContainer>
            </StyledMobileInputs>
            {hasKeywordFilters && (
                <StyledMobileKeywordChips>
                    {keywords.map((keyword) => (
                        <StyledKeywordChip>
                            <StyledFilterChip
                                key={`auctions-near-me-keyword-filter-${keyword}`}
                                onRemove={() => {
                                    removeKeyword(keyword);
                                }}
                            >
                                {keyword}
                            </StyledFilterChip>
                        </StyledKeywordChip>
                    ))}
                </StyledMobileKeywordChips>
            )}
        </span>
    ) : null;

    return (
        <StyledFilterBarLayout data-testid="auctionsSection">
            <div>
                {React.cloneElement(
                    <StyledResultsCount data-testid="auctionCount">
                        {numberOfAuctions === 1 ? (
                            <FormattedMessage id="nearby_auctions_count_single" />
                        ) : (
                            <FormattedMessage
                                id="nearby_auctions_count"
                                values={{
                                    numberOfAuctions: Intl.NumberFormat(locale).format(numberOfAuctions),
                                }}
                            />
                        )}
                    </StyledResultsCount>,
                    {
                        h2: isMobile,
                        h3: !isMobile,
                    }
                )}
            </div>
            {desktopLayout || mobileLayout}
        </StyledFilterBarLayout>
    );
};

export default AuctionsNearMeFilterBar;

const StyledFilterBarLayout = styled.section`
    display: flex;
    flex-direction: column;
    align-content: center;
    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        margin-top: 20px;
    }
    @media (min-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidthNum + 1}px) {
        margin-top: 0;
    }
`;

const StyledDesktopLayout = styled.span`
    @media (min-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidthNum + 1}px) {
        display: flex;
        flex-wrap: wrap;
        margin-top: 12px;
    }
    [data-testid='text'] {
        ${({ theme }) => css`
            font-family: ${theme.typography.fontPrimary};
            color: ${theme.colors.grey300};
        `}
        font-size: 12px;
        letter-spacing: 1px;
        line-height: 18px;
        vertical-align: middle;
    }
    [data-testid='select'] {
        width: 90px;
    }
`;

const StyledDesktopInputs = styled.span`
    display: flex;
    margin-top: 8px;
`;

const StyledMobileInputs = styled.div`
    margin-top: 8px;
`;

const StyledFilterChipsContainer = styled.span`
    display: flex;
    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        margin-top: 8px;
    }
`;

const StyledResultsCount = styled(Text)`
    flex-grow: 1;
    align-self: center;
    margin-right: 8px;
    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        margin-top: 32px;
    }
`;

const StyledFilterChip = styled(FilterChip)`
    max-width: 77px;
`;

const StyledKeywordChip = styled.div`
    margin-top: 8px;
`;

const StyledMobileKeywordChips = styled.div`
    display: flex;
    flex-wrap: wrap;
    margin-bottom: 8px;
`;
